/**
 * Account Reviews List Page
 * Shows all account reviews with search, filters, and sorting
 */

import React, { useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getAccountReviews } from "../services/salesTeam.service";
import Button from "../components/common/Button";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Badge from "../components/ui/Badge";
import {
  PlusIcon,
  MagnifyingGlassIcon,
  ClipboardDocumentCheckIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import type { ReviewStatus, AccountReviewFilters } from "../types/sales.types";
import { REVIEW_STATUS_OPTIONS } from "../types/sales.types";

const statusColors: Record<ReviewStatus, string> = {
  draft: "gray",
  submitted: "blue",
  needs_revision: "yellow",
  approved: "green",
};

const AccountReviews: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const positionId = searchParams.get("positionId") || undefined;
  const orgId = searchParams.get("orgId") || undefined;

  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState<"review_date" | "organization">(
    "review_date",
  );
  const [sortAsc, setSortAsc] = useState(false);

  // Debounce search
  React.useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const filters: AccountReviewFilters = {
    organization_id: orgId,
    status: statusFilter !== "all" ? [statusFilter as ReviewStatus] : undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  };

  const {
    data: rawReviews,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["account-reviews", filters],
    queryFn: () => getAccountReviews(filters),
  });

  // Client-side search and sort
  const reviews = useMemo(() => {
    let result = rawReviews || [];

    // Search by org name
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (r) =>
          r.organization?.name?.toLowerCase().includes(q) ||
          r.submitted_by_profile?.full_name?.toLowerCase().includes(q),
      );
    }

    // Sort
    result = [...result].sort((a, b) => {
      if (sortField === "review_date") {
        const dateA = a.review_date || "";
        const dateB = b.review_date || "";
        return sortAsc
          ? dateA.localeCompare(dateB)
          : dateB.localeCompare(dateA);
      }
      const nameA = a.organization?.name || "";
      const nameB = b.organization?.name || "";
      return sortAsc ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    });

    return result;
  }, [rawReviews, searchQuery, sortField, sortAsc]);

  const handleSort = (field: "review_date" | "organization") => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(field === "organization"); // org defaults asc, date defaults desc
    }
  };

  const SortIcon: React.FC<{ field: string }> = ({ field }) => {
    if (sortField !== field) {
      return null;
    }
    return sortAsc ? (
      <ChevronUpIcon className="h-3 w-3 inline ml-1" />
    ) : (
      <ChevronDownIcon className="h-3 w-3 inline ml-1" />
    );
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-700 p-4 rounded-lg">
          Failed to load account reviews. Please try again.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Account Reviews</h1>
          <p className="text-sm text-gray-500 mt-1">
            Field visit reviews and retailer account check-ins
          </p>
        </div>
        <Button
          onClick={() =>
            navigate(
              `/account-reviews/create${positionId ? `?positionId=${positionId}` : ""}`,
            )
          }
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          New Review
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by organization or submitter..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="all">All Statuses</option>
          {REVIEW_STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-500">From:</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-500">To:</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : reviews.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <ClipboardDocumentCheckIcon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
          <p className="font-medium text-gray-500">No account reviews yet</p>
          <p className="text-sm text-gray-400 mt-1">
            Create a new review after visiting a retailer location
          </p>
          <Button
            className="mt-4"
            onClick={() => navigate("/account-reviews/create")}
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            New Review
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort("organization")}
                >
                  Organization
                  <SortIcon field="organization" />
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700"
                  onClick={() => handleSort("review_date")}
                >
                  Review Date
                  <SortIcon field="review_date" />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Submitted By
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Reviewer
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {reviews.map((review) => (
                <tr
                  key={review.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/account-reviews/${review.id}`)}
                >
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-gray-900">
                      {review.organization?.name || "Unknown"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {review.review_date
                      ? new Date(review.review_date).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {review.submitted_by_profile?.full_name || "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {review.reviewer_profile?.full_name || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      color={statusColors[review.status] || "gray"}
                      size="sm"
                    >
                      {REVIEW_STATUS_OPTIONS.find(
                        (o) => o.value === review.status,
                      )?.label || review.status}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AccountReviews;
